import { skipToken } from "@tanstack/react-query";
import { useMemo } from "react";

import { useAsyncDataFromQuery } from "@tsmono/react/hooks";
import {
  AsyncData,
  data as asyncData,
  map as mapAsyncData,
} from "@tsmono/util";

import { getApi } from "../app_config";
import {
  ClientAPI,
  LogDetails,
  LogInfo,
  PendingSampleResponse,
  PendingSamples,
  RunningMetric,
} from "../client/api/types";
import { queryClient } from "../state/queryClient";

import { fetchEngine } from "./fetchEngine";
import { useLogHeader } from "./log";

const kDefaultRefreshSeconds = 2;

export const pendingSamplesKey = (
  logDir: string,
  logFile: string | undefined
) => ["log_data", "pending-samples", logDir, logFile ?? null] as const;

/**
 * A running eval's sample buffer (pending sample summaries + running metrics)
 * as a poll-driven react-query query keyed on `(logDir, logFile)`. Polling has
 * no imperative start/stop: enablement derives from the log's live status,
 * cadence from the server's refresh hint, and teardown from the query key
 * changing. Each tick threads the previous data's etag, and probes the log
 * file itself (a cheap `get_log_info`) so the completed summaries / status
 * are re-read whenever the file has actually changed — the convergence loop
 * that keeps the settled side of the merge fresh alongside the buffer.
 */

export interface PendingSamplesPollInputs {
  logFile: string | undefined;
  logStatus: LogDetails["status"] | undefined;
  apiSupportsPendingSamples: boolean;
}

/** Poll while a log is given, still running, and the api has a buffer. */
export const shouldPollPendingSamples = (
  inputs: PendingSamplesPollInputs
): boolean =>
  inputs.logFile !== undefined &&
  inputs.logStatus === "started" &&
  inputs.apiSupportsPendingSamples;

export const pendingSamplesIntervalMs = (
  data: PendingSamples | null | undefined
): number => (data?.refresh ?? kDefaultRefreshSeconds) * 1000;

/**
 * The next cache value given a poll response. `NotFound` means the buffer is
 * gone (eval completed) or not written yet (eval just started) — either way
 * there are no pending samples to show.
 */
export const nextPendingSamples = (
  prev: PendingSamples | null,
  response: PendingSampleResponse
): PendingSamples | null => {
  switch (response.status) {
    case "OK":
      return response.pendingSamples ?? prev;
    case "NotModified":
      return prev;
    case "NotFound":
      return null;
  }
};

const detailsSignatureKey = (logDir: string, logFile: string) =>
  ["log_data", "details-signature", logDir, logFile] as const;

const logInfoSignature = (info: LogInfo): string =>
  `${info.size}:${info.etag ?? ""}`;

/**
 * Re-read the log's details iff the file changed since the last read this
 * poll issued. The signature is recorded only after the read settles: a read
 * issued after observing a signature reflects at least that state (the log
 * only grows mid-run), so a read that races a concurrent flush leaves the
 * next tick's probe mismatched and re-reads — buffer transitions alone can't
 * be the trigger, because a sample leaves the buffer before its flush is
 * readable. `fresh` bypasses the memoized remote-file snapshot, which would
 * otherwise re-serve pre-change content.
 */
const refreshDetailsOnChange = async (
  api: ClientAPI,
  logDir: string,
  logFile: string
): Promise<void> => {
  const signature = logInfoSignature(await api.get_log_info(logFile));
  const key = detailsSignatureKey(logDir, logFile);
  if (queryClient.getQueryData<string>(key) === signature) {
    return;
  }
  await fetchEngine.ensure(logFile, {
    depth: "detailed",
    priority: "elevated",
    fresh: true,
  });
  queryClient.setQueryData(key, signature);
};

/** One poll tick (the queryFn; exported for tests). */
export const fetchPendingSamples = async (
  api: ClientAPI,
  logDir: string,
  logFile: string
): Promise<PendingSamples | null> => {
  const getPendingSamples = api.get_log_pending_samples;
  if (!getPendingSamples) {
    throw new Error("API does not support pending samples");
  }
  const prev =
    queryClient.getQueryData<PendingSamples | null>(
      pendingSamplesKey(logDir, logFile)
    ) ?? null;
  const response = await getPendingSamples(logFile, prev?.etag);
  // Awaited so a NotFound tick drops the pending rows only once the fresh
  // summaries/status (which may end the poll) are in.
  await refreshDetailsOnChange(api, logDir, logFile);
  return nextPendingSamples(prev, response);
};

/**
 * The pending samples for a log, polled while it is running. Settles to
 * `data(undefined)` when there is nothing pending (log not running, api
 * without a buffer, buffer gone). A parked poll (ticks exhausted their
 * retries) surfaces as `error`. Polling lifetime is subscriber lifetime:
 * every dependent calls this to declare its own dependency.
 */
export const usePendingSamples = (
  logDir: string,
  logFile: string | undefined
): AsyncData<PendingSamples | undefined> => {
  const api = getApi();
  const liveStatus = useLogHeader(logDir, logFile, { demand: "passive" }).data
    ?.status;
  const enabled =
    shouldPollPendingSamples({
      logFile,
      logStatus: liveStatus,
      apiSupportsPendingSamples: api.get_log_pending_samples !== undefined,
    }) && logFile !== undefined;
  const result = useAsyncDataFromQuery({
    queryKey: pendingSamplesKey(logDir, logFile),
    queryFn: enabled
      ? () => fetchPendingSamples(api, logDir, logFile)
      : skipToken,
    // A failed tick retries per the client default, then parks the query in
    // error state; stopping the interval there is the give-up.
    refetchInterval: (query) =>
      query.state.status === "error"
        ? false
        : pendingSamplesIntervalMs(query.state.data),
    refetchIntervalInBackground: true,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
  // Gate the read on enablement: once the log stops running, the final
  // details refresh owns the summaries and any cached buffer data is stale.
  return useMemo(
    () =>
      enabled
        ? mapAsyncData(result, (data) => data ?? undefined)
        : asyncData(undefined),
    [enabled, result]
  );
};

/**
 * A running eval's live metrics. `data(undefined)` when the log isn't
 * running or no metrics have been reported yet. That the metrics travel in
 * the pending-samples buffer is subsystem-private.
 */
export const useRunningMetrics = (
  logDir: string,
  logFile: string | undefined
): AsyncData<RunningMetric[] | undefined> => {
  const pending = usePendingSamples(logDir, logFile);
  return useMemo(
    () => mapAsyncData(pending, (data) => data?.metrics),
    [pending]
  );
};

/**
 * Non-React snapshot of the pending samples (for the running-sample query's
 * finalize decision). Returns
 * `undefined` when there's no resolved dir.
 */
export const getPendingSamples = (
  logDir: string | undefined,
  logFile: string
): PendingSamples | undefined =>
  logDir === undefined
    ? undefined
    : (queryClient.getQueryData<PendingSamples | null>(
        pendingSamplesKey(logDir, logFile)
      ) ?? undefined);
